import {
  clearFiltersStorage,
  deleteAccessToken,
  queryClient,
} from '@zenos/front-end-util-utils';
import type { PropsWithChildren } from 'react';
import { createContext, useEffect, useMemo, useReducer } from 'react';
import type { AuthSession } from '../api/auth';
import {
  clearAuthSessionActive,
  isAuthSessionValid,
  markAuthSessionActive,
} from '../util/authSession';

const AUTH_SESSION_VERSION_KEY = 'zenosAuthSessionVersion';
const AUTH_SESSION_FINGERPRINT_KEY = `${AUTH_SESSION_VERSION_KEY}Fingerprint`;

const generateRandomId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return Math.random().toString(36).slice(2, 11);
};

/** Stable per login session — refresh keeps the same expiry; a new login gets a new one. */
const getSessionIdentityKey = (token?: AuthSession) => {
  if (!isAuthSessionValid(token?.expiry)) return '';
  return String(token!.expiry);
};

const clearCacheOnIdentityChange = (
  previousToken?: AuthSession,
  nextToken?: AuthSession,
) => {
  if (getSessionIdentityKey(previousToken) === getSessionIdentityKey(nextToken)) {
    return;
  }

  // Clear before React re-renders authenticated children, so queries start
  // once against an empty cache instead of being cancelled mid-flight.
  queryClient.clear();
};

const syncAuthSessionVersion = (expiry: number) => {
  if (typeof window === 'undefined') return;

  if (!isAuthSessionValid(expiry)) {
    sessionStorage.removeItem(AUTH_SESSION_VERSION_KEY);
    sessionStorage.removeItem(AUTH_SESSION_FINGERPRINT_KEY);
    return;
  }

  const nextFingerprint = String(expiry);
  const currentFingerprint = sessionStorage.getItem(
    AUTH_SESSION_FINGERPRINT_KEY,
  );
  const currentSessionVersion = sessionStorage.getItem(
    AUTH_SESSION_VERSION_KEY,
  );

  if (!currentSessionVersion || currentFingerprint !== nextFingerprint) {
    sessionStorage.setItem(AUTH_SESSION_VERSION_KEY, generateRandomId());
  }

  sessionStorage.setItem(AUTH_SESSION_FINGERPRINT_KEY, nextFingerprint);
};

type AuthContext = {
  clearToken(): void;
  setToken(payload: AuthContext['token']): void;
  token?: AuthSession;
};

const authActions = {
  clearToken: 'clearToken',
  setToken: 'setToken',
} as const;

export const AuthContext = createContext<AuthContext>({
  token: {
    expiry: 0,
  },
  clearToken: () => undefined,
  setToken: () => undefined,
});

const initialState: Pick<AuthContext, 'token'> = {
  token: { expiry: 0 },
};

const authReducer = (
  state: Pick<AuthContext, 'token'>,
  {
    type,
    token,
  }: { type: keyof typeof authActions } & Pick<AuthContext, 'token'>,
) => {
  switch (type) {
    case authActions.setToken:
      if (!token) return state;

      const isNewLogin = !isAuthSessionValid(state.token?.expiry);

      deleteAccessToken();
      syncAuthSessionVersion(token.expiry);
      markAuthSessionActive();

      if (isNewLogin) {
        clearFiltersStorage();
      }

      return { ...state, token };

    case authActions.clearToken:
      deleteAccessToken();
      syncAuthSessionVersion(0);
      clearAuthSessionActive();
      clearFiltersStorage();
      return {
        ...state,
        token: {
          expiry: 0,
        },
      };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    syncAuthSessionVersion(state.token?.expiry ?? 0);
  }, [state.token?.expiry]);

  const setToken = (token: AuthContext['token']) => {
    if (token) {
      clearCacheOnIdentityChange(state.token, token);
    }
    dispatch({ type: authActions.setToken, token });
  };

  const clearToken = () => {
    clearCacheOnIdentityChange(state.token, { expiry: 0 });
    dispatch({ type: authActions.clearToken });
  };

  const contextValue = useMemo(
    () => ({
      ...state,
      setToken,
      clearToken,
    }),
    [state],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
